import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { DashboardStats, AccountState, SystemConfig } from '../types'
import { authApi } from '../api/client'

// 用户类型
export interface User {
  id: string
  email: string
  name: string
  role: 'admin' | 'bd'
  is_active: boolean
  created_at: string
  last_login_at: string | null
}

// 认证状态
interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  impersonatedUserId: string | null
  impersonatedUser: User | null
  bdUsers: User[]

  // Actions
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  setImpersonation: (userId: string | null, user?: User | null) => void
  loadBdUsers: () => Promise<void>
  refreshUser: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      impersonatedUserId: null,
      impersonatedUser: null,
      bdUsers: [],

      login: async (email: string, password: string) => {
        const response = await authApi.login(email, password)
        const { access_token, user } = response

        // Store token in both localStorage and zustand store
        localStorage.setItem('auth_token', access_token)

        set({
          user,
          token: access_token,
          isAuthenticated: true,
          impersonatedUserId: null,
          impersonatedUser: null,
        })

        // If admin, load BD users list
        if (user.role === 'admin') {
          get().loadBdUsers()
        }
      },

      logout: () => {
        localStorage.removeItem('auth_token')
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          impersonatedUserId: null,
          impersonatedUser: null,
          bdUsers: [],
        })
      },

      setImpersonation: (userId: string | null, user?: User | null) => {
        set({
          impersonatedUserId: userId,
          impersonatedUser: user || null,
        })
      },

      loadBdUsers: async () => {
        try {
          const users = await authApi.getBdUsers()
          set({ bdUsers: users })
        } catch (error) {
          console.error('Failed to load BD users:', error)
        }
      },

      refreshUser: async () => {
        try {
          const user = await authApi.getMe()
          set({ user })
        } catch (error) {
          // Token invalid, logout
          get().logout()
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        impersonatedUserId: state.impersonatedUserId,
        impersonatedUser: state.impersonatedUser,
      }),
      onRehydrateStorage: () => (state) => {
        // When zustand rehydrates from localStorage, also restore auth_token
        if (state?.token) {
          localStorage.setItem('auth_token', state.token)
        }
      },
    }
  )
)

// 用户偏好设置
interface UserPreferences {
  sidebarCollapsed: boolean
  theme: 'light' | 'dark'
  tablePageSize: number
  autoRefreshInterval: number // 秒，0表示不自动刷新
  notificationEnabled: boolean
}

// 应用状态
interface AppState {
  // 侧边栏状态
  sidebarCollapsed: boolean
  setSidebarCollapsed: (collapsed: boolean) => void
  toggleSidebar: () => void

  // 用户偏好
  preferences: UserPreferences
  setPreferences: (prefs: Partial<UserPreferences>) => void

  // 仪表盘数据缓存
  dashboardStats: DashboardStats | null
  setDashboardStats: (stats: DashboardStats) => void

  // 账号状态缓存
  accounts: AccountState[]
  setAccounts: (accounts: AccountState[]) => void

  // 系统配置缓存
  systemConfig: SystemConfig | null
  setSystemConfig: (config: SystemConfig) => void

  // 未处理回复数量
  unprocessedRepliesCount: number
  setUnprocessedRepliesCount: (count: number) => void

  // 全局加载状态
  isLoading: boolean
  setIsLoading: (loading: boolean) => void

  // 全局错误
  globalError: string | null
  setGlobalError: (error: string | null) => void
  clearError: () => void
}

const defaultPreferences: UserPreferences = {
  sidebarCollapsed: false,
  theme: 'light',
  tablePageSize: 20,
  autoRefreshInterval: 30,
  notificationEnabled: true,
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      // 侧边栏状态
      sidebarCollapsed: false,
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      // 用户偏好
      preferences: defaultPreferences,
      setPreferences: (prefs) =>
        set((state) => ({
          preferences: { ...state.preferences, ...prefs },
        })),

      // 仪表盘数据
      dashboardStats: null,
      setDashboardStats: (stats) => set({ dashboardStats: stats }),

      // 账号状态
      accounts: [],
      setAccounts: (accounts) => set({ accounts }),

      // 系统配置
      systemConfig: null,
      setSystemConfig: (config) => set({ systemConfig: config }),

      // 未处理回复
      unprocessedRepliesCount: 0,
      setUnprocessedRepliesCount: (count) => set({ unprocessedRepliesCount: count }),

      // 全局加载
      isLoading: false,
      setIsLoading: (loading) => set({ isLoading: loading }),

      // 全局错误
      globalError: null,
      setGlobalError: (error) => set({ globalError: error }),
      clearError: () => set({ globalError: null }),
    }),
    {
      name: 'bd-automation-storage',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        preferences: state.preferences,
      }),
    }
  )
)

// 项目筛选状态
interface ProjectFilterState {
  status: string | null
  category: string | null
  chain: string | null
  minPriority: number | null
  maxPriority: number | null
  searchText: string
  scrapeFilter: string | null
  crmConflictFilter: string | null
  sortBy: string
  sortOrder: 'asc' | 'desc'
  page: number
  pageSize: number

  setFilter: (key: string, value: unknown) => void
  resetFilters: () => void
  setPage: (page: number) => void
  setPageSize: (size: number) => void
}

const defaultFilters = {
  status: null,
  category: null,
  chain: null,
  minPriority: null,
  maxPriority: null,
  searchText: '',
  scrapeFilter: null,
  crmConflictFilter: null,
  sortBy: 'updated_at',
  sortOrder: 'desc' as const,
  page: 1,
  pageSize: 20,
}

export const useProjectFilterStore = create<ProjectFilterState>((set) => ({
  ...defaultFilters,
  
  setFilter: (key, value) => set({ [key]: value, page: 1 }),
  resetFilters: () => set(defaultFilters),
  setPage: (page) => set({ page }),
  setPageSize: (pageSize) => set({ pageSize, page: 1 }),
}))

// 触达筛选状态
interface OutreachFilterState {
  channel: string | null
  status: string | null
  projectId: number | null
  dateRange: [string, string] | null
  infoFilter: string | null
  page: number
  pageSize: number

  setFilter: (key: string, value: unknown) => void
  resetFilters: () => void
}

export const useOutreachFilterStore = create<OutreachFilterState>((set) => ({
  channel: null,
  status: null,
  projectId: null,
  dateRange: null,
  infoFilter: null,
  page: 1,
  pageSize: 20,

  setFilter: (key, value) => set({ [key]: value, page: 1 }),
  resetFilters: () => set({
    channel: null,
    status: null,
    projectId: null,
    dateRange: null,
    infoFilter: null,
    page: 1,
    pageSize: 20,
  }),
}))

// 回复筛选状态
interface ReplyFilterState {
  channel: string | null
  intent: string | null
  isProcessed: boolean | null
  projectId: number | null
  page: number
  pageSize: number
  
  setFilter: (key: string, value: unknown) => void
  resetFilters: () => void
}

export const useReplyFilterStore = create<ReplyFilterState>((set) => ({
  channel: null,
  intent: null,
  isProcessed: false, // 默认显示未处理
  projectId: null,
  page: 1,
  pageSize: 20,
  
  setFilter: (key, value) => set({ [key]: value, page: 1 }),
  resetFilters: () => set({
    channel: null,
    intent: null,
    isProcessed: false,
    projectId: null,
    page: 1,
    pageSize: 20,
  }),
}))
